import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PlusCircle, ArrowLeft, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Work & Productivity',
  'Visual Studio',
  'Content & Social Media',
  'Business & Marketing',
  'Life & Personal Growth',
  'Coding & Technical',
  'Daily Trends (Featured)'
];

export default function AdminPanel() {
  const [prompts, setPrompts] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAndFetchPrompts();
  }, []);

  const checkAdminAndFetchPrompts = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const userResponse = await axios.get(`${BACKEND_URL}/api/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (userResponse.data.role !== 'admin') {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }

      fetchPrompts();
    } catch (error) {
      toast.error('Failed to verify admin access');
      navigate('/login');
    }
  };

  const fetchPrompts = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrompts(response.data);
    } catch (error) {
      toast.error('Failed to load prompts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !category || !content) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      if (editingId) {
        await axios.put(
          `${BACKEND_URL}/api/admin/prompts/${editingId}`,
          { title, category, content },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Prompt updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/prompts`,
          { title, category, content },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Prompt created successfully');
      }

      setTitle('');
      setCategory('');
      setContent('');
      setEditingId(null);
      setDialogOpen(false);
      fetchPrompts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prompt) => {
    setTitle(prompt.title);
    setCategory(prompt.category);
    setContent(prompt.content);
    setEditingId(prompt.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/prompts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prompt deleted successfully');
      fetchPrompts();
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };

  const handleNewPrompt = () => {
    setTitle('');
    setCategory('');
    setContent('');
    setEditingId(null);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen" style={{background: '#0a0a0a'}}>
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold" style={{fontFamily: 'Outfit', color: '#f59e0b'}}>Admin Panel</h1>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleNewPrompt} data-testid="add-prompt-btn">
                <PlusCircle className="w-4 h-4 mr-2" /> Add New Prompt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Outfit'}}>
                  {editingId ? 'Edit Prompt' : 'Add New Prompt'}
                </DialogTitle>
                <DialogDescription>
                  {editingId ? 'Update the prompt details below' : 'Create a new prompt for the library'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter prompt title"
                    required
                    data-testid="prompt-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={category} onValueChange={setCategory} required>
                    <SelectTrigger data-testid="prompt-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Prompt Content</Label>
                  <Textarea
                    id="content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter the full prompt text"
                    rows={8}
                    required
                    data-testid="prompt-content-textarea"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    data-testid="cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="save-prompt-btn">
                    {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Prompts List */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold mb-2" style={{fontFamily: 'Outfit'}}>All Prompts ({prompts.length})</h2>
          <p className="text-muted-foreground">Manage your prompt library</p>
        </div>

        <div className="space-y-4" data-testid="admin-prompts-list">
          {prompts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No prompts yet. Create your first prompt!</p>
              </CardContent>
            </Card>
          ) : (
            prompts.map((prompt) => (
              <Card key={prompt.id} className="hover:border-primary/50 transition-colors" data-testid={`admin-prompt-card-${prompt.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2" style={{fontFamily: 'Outfit'}}>{prompt.title}</CardTitle>
                      <div className="flex gap-2 items-center text-sm text-muted-foreground">
                        <span className="px-2 py-1 rounded" style={{background: '#262626', color: '#f59e0b'}}>
                          {prompt.category}
                        </span>
                        <span>•</span>
                        <span>
                          {new Date(prompt.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEdit(prompt)}
                        data-testid={`edit-prompt-${prompt.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDelete(prompt.id)}
                        data-testid={`delete-prompt-${prompt.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{prompt.content}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
